import React, { createContext, useContext, useState, useCallback } from 'react';
import type { NafathAddress } from '@services/api';

const SIGNUP_FLOW_KEY = 'saee:signup-flow';

export type SignupFlowNafathResult = {
  nationalAddresses?: NafathAddress[];
  fullName?: string;
  fullNameAr?: string;
  name?: string;
  email?: string;
};

type SignupFlowState = {
  signUpPhone: string;
  nafathResult: SignupFlowNafathResult | null;
};

type SignupFlowContextValue = SignupFlowState & {
  setSignUpPhone: (phone: string) => void;
  setNafathResult: (result: SignupFlowNafathResult | null) => void;
  clearSignupFlow: () => void;
};

const SignupFlowContext = createContext<SignupFlowContextValue | null>(null);

function loadPersistedFlow(): SignupFlowState {
  if (typeof window === 'undefined') return { signUpPhone: '', nafathResult: null };
  try {
    const raw = window.sessionStorage.getItem(SIGNUP_FLOW_KEY);
    if (!raw) return { signUpPhone: '', nafathResult: null };
    const parsed = JSON.parse(raw) as Partial<SignupFlowState>;
    return {
      signUpPhone: typeof parsed.signUpPhone === 'string' ? parsed.signUpPhone : '',
      nafathResult: parsed.nafathResult ?? null,
    };
  } catch {
    return { signUpPhone: '', nafathResult: null };
  }
}

function persistFlow(state: SignupFlowState): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SIGNUP_FLOW_KEY, JSON.stringify(state));
  } catch {
    // ignore quota
  }
}

export function SignupFlowProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SignupFlowState>(loadPersistedFlow);

  const setSignUpPhone = useCallback((phone: string) => {
    setState((prev) => {
      const next = { ...prev, signUpPhone: phone };
      persistFlow(next);
      return next;
    });
  }, []);

  const setNafathResult = useCallback((result: SignupFlowNafathResult | null) => {
    setState((prev) => {
      const next = { ...prev, nafathResult: result };
      persistFlow(next);
      return next;
    });
  }, []);

  const clearSignupFlow = useCallback(() => {
    const next: SignupFlowState = { signUpPhone: '', nafathResult: null };
    setState(next);
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.removeItem(SIGNUP_FLOW_KEY);
      } catch {
        // ignore
      }
    }
  }, []);

  return (
    <SignupFlowContext.Provider value={{ ...state, setSignUpPhone, setNafathResult, clearSignupFlow }}>
      {children}
    </SignupFlowContext.Provider>
  );
}

export function useSignupFlow(): SignupFlowContextValue {
  const ctx = useContext(SignupFlowContext);
  if (!ctx) throw new Error('useSignupFlow must be used inside SignupFlowProvider');
  return ctx;
}
